import { z } from "@/utils/form";

import { authMethodSearchSchema, contactMethodToMethodParam } from "../utils/authMethodSearchParam";

function stripSurroundingQuotes(value: string): string {
    const trimmed = value.trim();
    if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        return trimmed.slice(1, -1).trim();
    }
    return trimmed;
}

/** Query parsers may pass repeated keys or odd shapes as string[]. Normalise for Zod. */
function coerceSearchString(value: unknown): string | undefined {
    if (value === undefined || value === null) return undefined;
    if (typeof value === "string") {
        const normalized = stripSurroundingQuotes(value);
        return normalized === "" ? undefined : normalized;
    }
    if (Array.isArray(value)) {
        const first = value[0];
        if (first === undefined || first === null) return undefined;
        if (typeof first === "string") {
            const normalized = stripSurroundingQuotes(first);
            return normalized === "" ? undefined : normalized;
        }
        const normalized = stripSurroundingQuotes(String(first));
        return normalized === "" ? undefined : normalized;
    }
    return undefined;
}

const optionalCoercedStringSchema = z.preprocess(coerceSearchString, z.string().optional());
const optionalNullableCoercedStringSchema = z.preprocess(coerceSearchString, z.string().optional().nullable());
const requiredCoercedStringSchema = (minLen: number) => z.preprocess(coerceSearchString, z.string().min(minLen));

/**
 * Email OTP verification routes (different entry points, keep URLs stable):
 * - `/verify` – post-signup flow; EmailCodeEntry + optional loading gate.
 * - `/activate-account` – alternate entry; EmailCodeEntry layout.
 * - `/verify-email` – attaches email to anonymous session then OTP before `/set-password`.
 */

/** `/` and `/authenticate` – smart identifier entry before routing to login / confirm-account / new-user.
 * Both paths render the same UI; `/authenticate` exists for stable deep links and bookmarks.
 */
export const smartAuthLandingSearchSchema = z.object({
    method: authMethodSearchSchema,
    email: z.string().optional(),
    phone: z.string().optional(),
});

export type SmartAuthLandingSearch = z.infer<typeof smartAuthLandingSearchSchema>;

/** `/login` – password (email) vs SMS OTP (phone). */
export const loginRouteSearchSchema = z.object({
    email: optionalCoercedStringSchema,
    phone: optionalCoercedStringSchema,
    invited_by_name: optionalNullableCoercedStringSchema,
    /** Primary: `email` vs `phone`. Legacy `contactMethod` and `method=sms` still accepted. */
    method: authMethodSearchSchema,
    contactMethod: z.enum(["email", "phone"]).optional(),
    phoneOtp: z.enum(["1"]).optional(),
});

export type LoginRouteSearch = z.infer<typeof loginRouteSearchSchema>;

/** Keep only the active channel in `/login` search – avoids stale cross-field validation. */
export function buildLoginRouteSearch(params: {
    email?: string | null;
    phone?: string | null;
    invited_by_name?: string | null;
    contactMethod: "email" | "phone";
}): LoginRouteSearch {
    const search: LoginRouteSearch = {
        invited_by_name: params.invited_by_name ?? undefined,
        contactMethod: params.contactMethod,
        method: contactMethodToMethodParam(params.contactMethod),
    };
    if (params.contactMethod === "email" && params.email?.trim()) {
        search.email = params.email.trim();
    }
    if (params.contactMethod === "phone" && params.phone?.trim()) {
        search.phone = params.phone.trim();
    }
    return search;
}

/** `/confirm-account` – invite completion; same `method` convention as login. */
export const confirmAccountRouteSearchSchema = z.object({
    email: z.string().email().optional(),
    phone: z.string().optional(),
    invited_by_name: z.string().nullable().optional(),
    method: authMethodSearchSchema,
    contactMethod: z.enum(["email", "phone"]).optional(),
});

export type ConfirmAccountRouteSearch = z.infer<typeof confirmAccountRouteSearchSchema>;

/** `/forgot-password` */
export const forgotPasswordRouteSearchSchema = z.object({
    email: z.string().optional(),
    phone: z.string().optional(),
    method: authMethodSearchSchema,
});

export type ForgotPasswordRouteSearch = z.infer<typeof forgotPasswordRouteSearchSchema>;

/** `/change-password` (auth) — `source` kept for older invite links; routing no longer depends on it. */
export const changePasswordRouteSearchSchema = z.object({
    source: z.literal("confirm-account").optional(),
});

export type ChangePasswordRouteSearch = z.infer<typeof changePasswordRouteSearchSchema>;

/** `/new-user` – no profile found; preserves channel hints from smart-auth. */
export const newUserIndexRouteSearchSchema = z.object({
    email: z.string().optional(),
    phone: z.string().optional(),
    invited_by_name: z.string().optional().nullable(),
    method: authMethodSearchSchema,
    contactMethod: z.enum(["email", "phone"]).optional(),
});

export type NewUserIndexRouteSearch = z.infer<typeof newUserIndexRouteSearchSchema>;

/** `/verify`, `/activate-account` – email-only OTP confirmation param. */
export const verifyEmailOtpRouteSearchSchema = z.object({
    email: z.string().email(),
});

export type VerifyEmailOtpRouteSearch = z.infer<typeof verifyEmailOtpRouteSearchSchema>;

/** `/signup/verify-phone` – SMS OTP step after phone sign-up. */
export const signupVerifyPhoneRouteSearchSchema = z.object({
    phone: requiredCoercedStringSchema(4),
});

export type SignupVerifyPhoneRouteSearch = z.infer<typeof signupVerifyPhoneRouteSearchSchema>;

/** `/verify-email` – email + optional name prefill for anon verify-then-password flow. */
export const verifyEmailSignupRouteSearchSchema = z.object({
    email: z.string().email(),
    first_name: z.string().optional().default(""),
    last_name: z.string().optional().default(""),
});

export type VerifyEmailSignupRouteSearch = z.infer<typeof verifyEmailSignupRouteSearchSchema>;

/** `/verification-complete` – post OTP; copy differs for email vs SMS sign-up. */
export const verificationCompleteRouteSearchSchema = z.object({
    via: z.enum(["email", "sms"]).optional(),
});

export type VerificationCompleteRouteSearch = z.infer<typeof verificationCompleteRouteSearchSchema>;

/** `/login/verify` – SMS OTP step during login flow. */
export const loginVerifyRouteSearchSchema = z.object({
    phone: requiredCoercedStringSchema(4),
});

export type LoginVerifyRouteSearch = z.infer<typeof loginVerifyRouteSearchSchema>;

/** `/confirm-account/verify` – OTP step during confirm-account flow (email or SMS). */
export const confirmAccountVerifyRouteSearchSchema = z.object({
    phone: optionalCoercedStringSchema,
    email: optionalCoercedStringSchema,
    type: z.preprocess(coerceSearchString, z.enum(["email", "sms"])),
    inviteEmail: optionalCoercedStringSchema,
});

export type ConfirmAccountVerifyRouteSearch = z.infer<typeof confirmAccountVerifyRouteSearchSchema>;

/** `/forgot-password/verify` – OTP step during forgot-password flow (email or SMS). */
export const forgotPasswordVerifyRouteSearchSchema = z.object({
    phone: optionalCoercedStringSchema,
    email: z.preprocess(coerceSearchString, z.string().email().optional()),
    type: z.preprocess(coerceSearchString, z.enum(["email", "sms"])),
});

export type ForgotPasswordVerifyRouteSearch = z.infer<typeof forgotPasswordVerifyRouteSearchSchema>;
